/**
 * IAN Assist — Constants
 * Personas, intents, tones, languages, defaults.
 */

export const PERSONAS = [
  { key: 'claims-adjuster', label: 'Claims Adjuster' },
  { key: 'underwriter', label: 'Underwriter' },
  { key: 'broker', label: 'Broker' },
  { key: 'custom', label: 'Custom' },
];

export const INTENTS = [
  { key: 'summarize', label: 'Summarize' },
  { key: 'actions', label: 'Actions' },
  { key: 'reply', label: 'Reply' },
  { key: 'translate', label: 'Translate' },
  { key: 'rephrase', label: 'Rephrase' },
];

export const SUMMARY_STYLES = [
  { key: 'executive', label: 'Executive' },
  { key: 'brief', label: 'Brief' },
  { key: 'key-points', label: 'Key Points' },
  { key: 'action-items', label: 'Action Items' },
];

export const REPLY_TONES = [
  { key: 'professional', label: 'Professional' },
  { key: 'friendly', label: 'Friendly' },
  { key: 'formal', label: 'Formal' },
  { key: 'concise', label: 'Concise' },
];

export const COMPOSE_TONES = [
  { key: 'professional', label: 'Professional' },
  { key: 'friendly', label: 'Friendly' },
  { key: 'formal', label: 'Formal' },
  { key: 'casual', label: 'Casual' },
];

export const EMAIL_TYPES = [
  { key: 'general', label: 'General' },
  { key: 'meeting_request', label: 'Meeting Request' },
  { key: 'follow_up', label: 'Follow Up' },
  { key: 'introduction', label: 'Introduction' },
  { key: 'thank_you', label: 'Thank You' },
  { key: 'complaint', label: 'Complaint' },
];

export const LANGUAGES = [
  { key: 'en', label: 'English' },
  { key: 'es', label: 'Spanish' },
  { key: 'fr', label: 'French' },
  { key: 'de', label: 'German' },
  { key: 'ja', label: 'Japanese' },
  { key: 'zh', label: 'Chinese' },
  { key: 'hi', label: 'Hindi' },
  { key: 'ar', label: 'Arabic' },
];

export const CARD_TYPES = {
  SUMMARY: 'summary',
  ACTION_ITEMS: 'action_items',
  DRAFT_REPLY: 'draft_reply',
  TRANSLATION: 'translation',
  REPHRASE: 'rephrase',
  COMPOSE: 'compose',
  EXECUTION_STATUS: 'execution_status',
};

export const ACTION_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  APPROVAL_REQUIRED: 'approval_required',
};

export const TRIGGER_TYPES = {
  MAIL_OPENED: 'mail_opened',
  MANUAL: 'manual',
  COMPOSE: 'compose',
};

// Injected at build time via webpack DefinePlugin (reads from .env)
// 'dev' uses /webhook-test/ paths  |  'prod' uses /webhook/ paths
/* global __N8N_MODE__, __N8N_BASE_URL__ */
export const N8N_MODE = typeof __N8N_MODE__ !== 'undefined' ? __N8N_MODE__ : 'dev';
export const N8N_BASE_URL = typeof __N8N_BASE_URL__ !== 'undefined' ? __N8N_BASE_URL__ : '';
export const N8N_WEBHOOK_PREFIX = N8N_MODE === 'prod' ? '/webhook/' : '/webhook-test/';

// Use the webpack dev-server proxy to avoid CORS preflight issues.
// The proxy rewrites /n8n-proxy/* → N8N_BASE_URL/* so the browser sees same-origin.
// Always use proxy when running on localhost (dev-server), regardless of N8N_MODE.
const IS_LOCALHOST = typeof window !== 'undefined' && window.location?.hostname === 'localhost';
export const N8N_PROXY_BASE = IS_LOCALHOST ? '/n8n-proxy' : N8N_BASE_URL;

export const DEFAULT_CONFIG = {
  n8nUrl: N8N_PROXY_BASE + N8N_WEBHOOK_PREFIX + 'dispatch',
  persona: 'claims-adjuster',
  autoTrigger: true,
  enabledIntents: ['summarize', 'actions', 'reply'],
  summaryStyle: 'executive',
  replyTone: 'professional',
  targetLanguage: 'en',
  // Per-persona workflow selection: { 'claims-adjuster': 'triage', 'broker': 'summary-then-reply' }
  activeWorkflow: {},
  // Per-persona user-built DAGs: { 'claims-adjuster': [ { id, name, dag, output_cards } ] }
  customWorkflows: {},
};

/**
 * Catalog of persona workflows the dispatcher knows about.
 * Mirrors n8n master-dispatcher.json PERSONA_WORKFLOWS.
 * Used to populate the Workflow dropdown in the header.
 */
export const PERSONA_WORKFLOW_CATALOG = {
  'claims-adjuster': [
    { id: 'triage', name: 'Triage (summary + actions + draft)' },
    { id: 'polish-roundtrip', name: 'Round-trip translate + reply' },
  ],
  broker: [
    { id: 'summary-then-reply', name: 'Summary → Draft reply' },
  ],
  underwriter: [
    { id: 'summary-actions', name: 'Summary + Actions (parallel)' },
  ],
  custom: [],
};

export const ENDPOINTS = {
  EMAIL_AGENT: N8N_WEBHOOK_PREFIX + 'email-agent',
  EXECUTE_ACTION: N8N_WEBHOOK_PREFIX + 'execute-action',
  CLASSIFY_INTENT: N8N_WEBHOOK_PREFIX + 'classify-intent',
};
